import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { Country, State } from 'country-state-city';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BillingInformation } from '../../models';
import { fromBillingInformation } from '../../store';
import { useReadOnlyMode } from '../../routes';
import { BillingInformationComponent } from '../../components';
import { isBillingInformationComplete } from '../../components/payment-information-section/payment-billing/BillingDetailsForm';

const useStyles = makeStyles(() => ({
  // Header, Edit link and Cancel / Save buttons as in the checkout's Billing information
  // (PaymentBillingSection, design 6.10).
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 32,
    margin: '30px 0 12px',
  },
  headingGroup: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 12,
    rowGap: 2,
    minWidth: 0,
  },
  sectionTitle: {
    margin: 0,
    '&&': {
      fontFamily: "'Inter Tight', sans-serif",
    },
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '-.01em',
    lineHeight: 1.3,
    color: '#16141d',
  },
  subheading: {
    margin: 0,
    fontSize: 11,
    lineHeight: 1.4,
    color: '#5b6661',
  },
  savedPill: {
    alignSelf: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    color: '#0f8461',
    backgroundColor: '#e7f8f1',
    border: '1px solid #b6ebd6',
    padding: '3px 10px',
    borderRadius: 999,
  },
  actionLink: {
    flex: 'none',
    minHeight: 32,
    padding: '0 4px',
    border: 'none',
    background: 'none',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#0b6b4f',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:disabled': {
      color: '#9aa5a0',
      cursor: 'not-allowed',
      textDecoration: 'none',
    },
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 'none',
  },
  ghostButton: {
    height: 32,
    minWidth: 72,
    padding: '0 12px',
    border: 'none',
    borderRadius: 8,
    background: 'none',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#0b6b4f',
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
      backgroundColor: '#f3faf6',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  primaryButton: {
    height: 32,
    minWidth: 64,
    padding: '0 14px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    border: '1px solid #0f8461',
    borderRadius: 8,
    backgroundColor: '#0f8461',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#ffffff',
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
      backgroundColor: '#0b6b4f',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  // Design 6.10: the same two-column summary as the checkout's billing block.
  summaryCard: {
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px 16px',
    padding: 16,
    border: '1px solid #e1e7e4',
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  summaryItem: {
    minWidth: 0,
  },
  summaryItemFull: {
    gridColumn: '1 / -1',
    minWidth: 0,
  },
  summaryLabel: {
    fontSize: 12,
    color: '#5b6661',
  },
  summaryValue: {
    margin: '2px 0 0',
    fontSize: 14,
    color: '#16141d',
    overflowWrap: 'anywhere',
  },
  // Payment & billing redesign form styling, scoped to the embedded billing
  // block — mirrors the checkout redesign so both surfaces stay consistent.
  formScope: {
    '& #billing-information': {
      paddingTop: 0,
    },
    '& #billing-information .MuiTypography-h3, & #billing-information .MuiTypography-h6': {
      fontFamily: "'Inter Tight', sans-serif",
      fontSize: 15,
      fontWeight: 700,
      color: '#16141d',
    },
    '& #billing-information .MuiTypography-subtitle2': {
      fontSize: 12.8,
      color: '#5b6661',
      fontStyle: 'normal',
    },
    '& #billing-information .MuiTypography-body1': {
      fontSize: 14,
      fontWeight: 600,
      color: '#16141d',
      marginBottom: 6,
      '& span': {
        color: '#d14343',
        fontWeight: 600,
      },
    },
    // Same 36px / 10px-radius inputs as the checkout's billing form.
    '& #billing-information .MuiOutlinedInput-root': {
      height: 36,
      minHeight: 36,
      borderRadius: 10,
      fontSize: 14,
      color: '#16141d',
      backgroundColor: '#fff',
      transition: 'border-color .15s ease, box-shadow .15s ease',
    },
    '& #billing-information .MuiOutlinedInput-input': {
      padding: '0 14px',
      '&::placeholder': {
        color: '#7f8b86',
        opacity: 1,
      },
    },
    '& #billing-information .MuiAutocomplete-inputRoot': {
      padding: '0 36px 0 14px',
    },
    '& #billing-information .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
      padding: 0,
    },
    '& #billing-information .MuiAutocomplete-endAdornment': {
      right: 12,
    },
    '& #billing-information .MuiFormHelperText-root': {
      margin: '6px 0 0',
      fontSize: 12,
      lineHeight: 1.4,
      color: '#d14343',
    },
    '& #billing-information .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e1e7e4',
    },
    '& #billing-information .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#cdd6d1',
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(15,132,97,.15)',
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0f8461',
      borderWidth: 1,
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e5484d',
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-error': {
      backgroundColor: '#fff6f6',
    },
    '& #billing-information .MuiOutlinedInput-root:has(input:-webkit-autofill)': {
      backgroundColor: '#e8f0fe',
    },
    '& #billing-information input:-webkit-autofill, & #billing-information input:-webkit-autofill:hover, & #billing-information input:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 1000px #e8f0fe inset',
      WebkitTextFillColor: '#16141d',
      caretColor: '#16141d',
      borderRadius: 'inherit',
    },
  },
}));

const CheckIcon = () => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// Design 6.10 order and labels (shared with the checkout's billing block).
const SUMMARY_FIELDS: Array<{
  key: keyof BillingInformation;
  labelId: string;
  full?: boolean;
  /** Shown only when the address has company details, as on checkout. */
  company?: boolean;
}> = [
  { key: 'ccEmail', labelId: 'page.order.confirmation.v2.billing.email' },
  { key: 'country', labelId: 'page.order.confirmation.v2.billing.country' },
  { key: 'address1', labelId: 'page.order.confirmation.v2.billing.address', full: true },
  { key: 'city', labelId: 'page.order.confirmation.v2.billing.city' },
  { key: 'state', labelId: 'page.order.confirmation.v2.billing.state' },
  { key: 'postalCode', labelId: 'page.order.confirmation.v2.billing.postal' },
  { key: 'fullBusinessName', labelId: 'page.order.confirmation.v2.billing.company.name', company: true },
  { key: 'vatNumber', labelId: 'page.order.confirmation.v2.billing.company.tax', company: true },
];

interface BillingInfoSectionProps {
  accountId: number;
}

export const BillingInfoSection = ({ accountId }: BillingInfoSectionProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const isReadOnly = useReadOnlyMode();
  const currentBillingInformation = useSelector(
    fromBillingInformation.selectOneByAccountId(accountId),
  );
  const isNotSyncedBillingInfo = !!currentBillingInformation && !currentBillingInformation.synced;
  const billingRef = useRef<any>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // The saved pill is transient feedback — hide it again after a short while.
  useEffect(() => {
    if (!justSaved) return undefined;
    const timer = setTimeout(() => setJustSaved(false), 5000);
    return () => clearTimeout(timer);
  }, [justSaved]);

  const hasSaved = !!currentBillingInformation;
  const hasCompany = !!(currentBillingInformation?.fullBusinessName
    || currentBillingInformation?.vatNumber);
  const summaryFields = SUMMARY_FIELDS.filter(({ company }) => !company || hasCompany);
  // Out-of-sync stored addresses must be re-confirmed, so force the form open.
  // Missing a required field (e.g. City, saved before it was collected): review it like an
  // out-of-sync address — the form opens, with nothing to cancel back to.
  const needsReview = isNotSyncedBillingInfo
    || (hasSaved && !isBillingInformationComplete(currentBillingInformation));
  const isEditMode = editing || !hasSaved || needsReview;

  const handleSave = async () => {
    if (!billingRef.current) return;
    setSaving(true);
    try {
      const saved: boolean = await billingRef.current.validateAndSave();
      if (saved) {
        setEditing(false);
        setJustSaved(true);
      }
    } finally {
      setSaving(false);
    }
  };

  // Empty values render as "—" in the summary, as on checkout.
  const displayValue = (key: keyof BillingInformation): string => {
    const raw = (currentBillingInformation?.[key] ?? '').toString();
    if (!raw) return '';
    if (key === 'country') {
      return Country.getCountryByCode(raw)?.name ?? raw;
    }
    if (key === 'state' && currentBillingInformation?.country) {
      const state = State.getStatesOfCountry(currentBillingInformation.country)
        .find(it => it.isoCode === raw || it.name === raw);
      return state?.name ?? raw;
    }
    return raw;
  };

  return (
    <Box id="billing-info-section">
      <Box className={classes.headerRow}>
        <Box className={classes.headingGroup}>
          <Typography component="h2" className={classes.sectionTitle}>
            <FormattedMessage id="page.order.confirmation.v2.billing.title" />
          </Typography>
          <Typography component="p" className={classes.subheading}>
            <FormattedMessage id="page.order.confirmation.v2.billing.subtitle" />
          </Typography>
          {!isEditMode && justSaved && (
            <Box component="span" role="status" className={classes.savedPill}>
              <CheckIcon />
              <FormattedMessage id="page.payment.billing.saved" />
            </Box>
          )}
        </Box>
        {isEditMode
          ? (
            <Box className={classes.headerActions}>
              {/* A new or out-of-sync address has nothing to go back to: Save only. */}
              {hasSaved && !needsReview && (
                <button
                  type="button"
                  id="billing-info-cancel-btn"
                  className={classes.ghostButton}
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  <FormattedMessage id="page.order.confirmation.v2.cancel" />
                </button>
              )}
              <button
                type="button"
                id="billing-info-save-btn"
                className={classes.primaryButton}
                onClick={() => {
                  handleSave().catch(() => {
                  });
                }}
                disabled={saving || isReadOnly}
                aria-busy={saving}
              >
                {saving && <CircularProgress size={14} sx={{ color: 'currentColor' }} />}
                <FormattedMessage id="page.order.confirmation.v2.save" />
              </button>
            </Box>
          )
          : (
            <button
              type="button"
              id="billing-info-edit-btn"
              className={classes.actionLink}
              onClick={() => setEditing(true)}
              disabled={isReadOnly}
            >
              <FormattedMessage id="page.order.confirmation.v2.edit" />
            </button>
          )}
      </Box>
      {isEditMode
        ? (
          <Box className={classes.formScope}>
            <BillingInformationComponent
              ref={billingRef}
              accountId={accountId}
              enableSave
              pageOwnsBilling
              redesign
              hideHeader
            />
          </Box>
        )
        : (
          <Box component="dl" className={classes.summaryCard} id="billing-info-summary">
            {summaryFields.map(({ key, labelId, full }) => (
              <Box key={key} className={full ? classes.summaryItemFull : classes.summaryItem}>
                <Box component="dt" className={classes.summaryLabel}>
                  {intl.formatMessage({ id: labelId })}
                </Box>
                <Box component="dd" className={classes.summaryValue}>
                  {displayValue(key) || '—'}
                </Box>
              </Box>
            ))}
          </Box>
        )}
    </Box>
  );
};
